import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';

import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';

import { Route } from '@/routes/routes.type';
import { useDisclosure, useRouteMatchParam } from '@/hooks';

import VehicleHistoryDetail from '@/components/History/VehiclesHistory/VehicleHistoryDetails';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import { Button, Checkbox, Divider, Modal, Space } from 'antd';
import { useState } from 'react';
import useFileLoading, { FILE_FORMAT, FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { useReportHandlers } from '@/hooks/useReportHandler';
import { useGenerateWorkOrderVehicleHistory } from '@/core/domain/reports/workOrderReport/workOrderReport.hooks';

type Props = { route: Route };

const VehicleHistoryDetailPage = ({ route }: Props) => {
  const intl = useIntl();
  const [printModalOpen, setPrintModalOpen] = useDisclosure();
  const [hasCommissionProductive, setHasCommissionProductive] = useState(false);
  const [hasReportValues, setHasReportValues] = useState(false);
  const [hasAuthorized, setHasAuthorized] = useState(false);

  const licensePlate = useRouteMatchParam('licensePlate');

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const generateReport = useGenerateWorkOrderVehicleHistory();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const result = await generateReport.mutateAsync({
        format: FILE_FORMAT[format],
        licensePlate,
        showAuthorized: hasAuthorized,
        showCommissioned: hasCommissionProductive,
        showValues: hasReportValues,
      });
      if (callback) {
        callback(result);
      }
    } catch {}
    setFileUnloading(format);
  };

  const { handleView, handlePdf } = useReportHandlers(
    `histórico_de_veículo_${licensePlate}`,
    handleGenerateReport,
  );

  return (
    <DefaultRouteTabs
      route={route.parent}
      baseKey={String(licensePlate)}
      basePath={`/app/history/vehiclesHistory/${licensePlate}/detail`}
      extra={
        <Button type="primary" onClick={setPrintModalOpen.open}>
          Imprimir
        </Button>
      }
    >
      <Helmet
        title={intl.formatMessage({
          id: route.name,
        })}
      />

      <VehicleHistoryDetail setHasAuthorized={setHasAuthorized} />

      <Divider />

      <SpaceForButton>
        <ButtonBack path="/app/history/vehiclesHistory" />
      </SpaceForButton>

      <Modal
        title="Histórico de veículo"
        open={printModalOpen}
        onCancel={setPrintModalOpen.close}
        okButtonProps={{ loading: fileLoading.REPORT_VIEW }}
        width={600}
        footer={
          <SpaceForButton justifyContent="flex-end">
            <Button block onClick={setPrintModalOpen.close}>
              Cancelar
            </Button>

            <Button
              block
              type="primary"
              onClick={handleView}
              key="viewReport"
              loading={fileLoading?.REPORT_VIEW}
            >
              Visualizar Relatório
            </Button>

            <Button
              block
              type="primary"
              onClick={handlePdf}
              key="PDF"
              loading={fileLoading?.PDF}
            >
              Gerar PDF
            </Button>
          </SpaceForButton>
        }
      >
        <Space direction="vertical">
          <Checkbox
            value={hasCommissionProductive}
            onChange={({ target: { checked } }) =>
              setHasCommissionProductive(checked)
            }
          >
            Exibir relatórios com produtivo comissionado
          </Checkbox>

          <Checkbox
            value={hasReportValues}
            onChange={({ target: { checked } }) => setHasReportValues(checked)}
          >
            Exibir relatórios com valores
          </Checkbox>
        </Space>
      </Modal>
    </DefaultRouteTabs>
  );
};

export default VehicleHistoryDetailPage;
